/**
 * AI Recovery Debug Utilities
 *
 * Exposed to window.debugAIRecovery for browser console usage
 * Allows manual debugging and recovery of AI generation issues
 */

import {
  manualRecoverMeeting,
  manualScan,
  getRecoveryHistory,
  getMonitoredStuckMeetings,
  getDetectorStatus,
} from "@/services/ai-stuck-detector";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

export const AIRecoveryDebug = {
  /**
   * Manually recover a specific meeting
   * Usage: debugAIRecovery.recoverMeeting('meeting-id')
   */
  async recoverMeeting(meetingId: string) {
    console.log(`[Debug] Fetching meeting ${meetingId}...`);

    const { data: meeting, error } = await supabase
      .from('meetings')
      .select('id, transcription, title')
      .eq('id', meetingId)
      .single();

    if (error) {
      console.error('[Debug] Failed to fetch meeting:', error);
      return;
    }

    if (!meeting?.transcription) {
      console.error('[Debug] Meeting has no transcription');
      return;
    }

    console.log(`[Debug] Recovering meeting: "${meeting.title}" (${meeting.transcription.length} chars)`);

    const result = await manualRecoverMeeting(meetingId, meeting.transcription);

    if (result.success) {
      console.log('✅ Recovery succeeded! Refetch the meeting to see updates.');
    } else {
      console.error('❌ Recovery failed:', result.error);
    }

    return result;
  },

  /**
   * Manually scan for stuck meetings
   * Usage: debugAIRecovery.scan()
   */
  async scan() {
    console.log('[Debug] Scanning for stuck meetings...');
    const reports = await manualScan();

    if (reports.length === 0) {
      console.log('✅ No stuck meetings found');
    } else {
      console.log(`⚠️ Found ${reports.length} stuck meetings:`);
      reports.forEach(report => {
        console.log(`  - ${report.meetingId} (${report.ageMinutes}min old, missing: ${report.hasMissing.join(', ')})`);
      });
    }

    return reports;
  },

  /**
   * Check the current status of a meeting's AI content
   * Usage: debugAIRecovery.checkMeeting('meeting-id')
   */
  async checkMeeting(meetingId: string) {
    console.log(`[Debug] Checking meeting ${meetingId}...`);

    const { data: meeting, error } = await supabase
      .from('meetings')
      .select('id, title, transcription, summary, action_items, notes, created_at, updated_at')
      .eq('id', meetingId)
      .single();

    if (error) {
      console.error('[Debug] Failed to fetch meeting:', error);
      return;
    }

    const missing: string[] = [];
    if (!meeting?.summary || meeting.summary.trim().length < 50) missing.push('summary');
    if (!meeting?.action_items || meeting.action_items.trim().length < 20) missing.push('action_items');
    if (!meeting?.notes || meeting.notes.trim().length < 50) missing.push('notes');

    console.log(`📋 Meeting: "${meeting.title}"`);
    console.log(`   Transcription: ${meeting.transcription?.length || 0} chars`);
    console.log(`   Summary: ${meeting.summary ? `✅ ${meeting.summary.length} chars` : '❌ Missing'}`);
    console.log(`   Action Items: ${meeting.action_items ? `✅ ${meeting.action_items.length} chars` : '❌ Missing'}`);
    console.log(`   Notes: ${meeting.notes ? `✅ ${meeting.notes.length} chars` : '❌ Missing'}`);

    if (missing.length > 0) {
      console.log(`\n⚠️ Missing: ${missing.join(', ')}`);
      console.log(`👉 To recover: debugAIRecovery.recoverMeeting('${meetingId}')`);
    } else {
      console.log(`\n✅ All AI content present!`);
    }

    return { missing, meeting };
  },

  /**
   * Get detailed recovery history for a specific meeting
   * Usage: debugAIRecovery.getHistory('meeting-id')
   */
  getHistory(meetingId: string) {
    const history = getRecoveryHistory(meetingId);
    if (!history) {
      console.log(`[History] No recovery history for meeting ${meetingId}`);
      return null;
    }

    console.log(`[History] Recovery history for ${meetingId}:`);
    console.log(`  Total attempts: ${history.recoveryAttempts}`);
    console.log(`  Consecutive failures: ${history.consecutiveFailures}`);
    console.log(`  Missing fields: ${history.missingFields.join(', ') || 'NONE'}`);

    if (history.lastSuccessfulRecovery) {
      const timeSinceSuccess = Math.round((Date.now() - history.lastSuccessfulRecovery) / 1000 / 60);
      console.log(`  Last successful recovery: ${timeSinceSuccess} minutes ago`);
    }

    if (history.lastRecoveryError) {
      console.log(`  Last error: ${history.lastRecoveryError}`);
    }

    if (history.recoveryTimestamps && history.recoveryTimestamps.length > 0) {
      console.log(`  Recovery attempt times:`);
      history.recoveryTimestamps.forEach((ts, i) => {
        const time = new Date(ts).toLocaleTimeString();
        console.log(`    ${i + 1}. ${time}`);
      });
    }

    return history;
  },

  /**
   * View all stuck meetings currently being monitored
   * Usage: debugAIRecovery.listMonitored()
   */
  listMonitored() {
    const meetings = getMonitoredStuckMeetings();

    if (meetings.length === 0) {
      console.log('[Monitored] No stuck meetings being monitored');
      return [];
    }

    console.log(`[Monitored] ${meetings.length} stuck meeting(s) being monitored:\n`);
    meetings.forEach(({ meetingId, info }) => {
      const timeSinceCheck = Math.round((Date.now() - info.timestamp) / 1000 / 60);
      console.log(`  📌 ${meetingId}`);
      console.log(`     Last checked: ${timeSinceCheck} min ago`);
      console.log(`     Recovery attempts: ${info.recoveryAttempts}, Consecutive failures: ${info.consecutiveFailures}`);
      console.log(`     Missing: ${info.missingFields.join(', ') || 'NONE'}`);
    });

    console.log();
    return meetings;
  },

  /**
   * Log current service status with detailed config
   * Usage: debugAIRecovery.status()
   */
  status() {
    const status = getDetectorStatus();
    console.log(`\n🤖 AI Stuck Detector Status:`);
    console.log(`   Running: ${status.running ? '✅ Yes' : '❌ No'}`);
    console.log(`   Description: ${status.description}`);
    console.log(`   Currently monitoring: ${status.monitoredMeetings} stuck meetings`);
    console.log(`\n⚙️ Configuration:`);
    console.log(`   Scan interval: ${status.configuration.scanIntervalMs / 1000}s`);
    console.log(`   Max age for recovery: ${status.configuration.maxAgeHours} hours`);
    console.log(`   Max recovery attempts: ${status.configuration.maxRecoveryAttempts}`);
    console.log();
    return status;
  },

  /**
   * Full diagnostic report for a stuck meeting
   * Usage: debugAIRecovery.diagnose('meeting-id')
   */
  async diagnose(meetingId: string) {
    console.log(`\n🔍 Full Diagnostic Report for ${meetingId}\n`);

    // Check meeting status
    await this.checkMeeting(meetingId);

    // Get recovery history
    console.log(`\n📊 Recovery History:`);
    this.getHistory(meetingId);

    // List similar stuck meetings
    const allMonitored = this.listMonitored();
    const similar = allMonitored.filter(m =>
      m.info.missingFields.join(',') ===
      getRecoveryHistory(meetingId)?.missingFields.join(',')
    );

    if (similar.length > 1) {
      console.log(`\n⚠️  Similar stuck meetings (${similar.length} total with same missing fields):`);
      similar.forEach(m => {
        if (m.meetingId !== meetingId) {
          console.log(`   - ${m.meetingId}`);
        }
      });
    }

    console.log(`\n💡 Suggestions:`);
    const history = getRecoveryHistory(meetingId);
    if (history?.consecutiveFailures === 0) {
      console.log(`   ✅ Most recent recovery was successful!`);
    } else if (history && history.consecutiveFailures >= 5) {
      console.log(`   ⚠️  Meeting has failed recovery ${history.consecutiveFailures}x in a row.`);
      console.log(`   Check the server logs: npx supabase functions logs generate-all-insights`);
    } else {
      console.log(`   🔄 Try recovery again: debugAIRecovery.recoverMeeting('${meetingId}')`);
    }
  },
};

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugAIRecovery = AIRecoveryDebug;
}

export default AIRecoveryDebug;
